/* eslint-disable no-await-in-loop */
import React, { useState } from 'react';
import moment from 'moment';
import api from 'api';
import { EOrganization, ESnapshot, EResponseTypes } from 'lib/types';
import { Badge, BadgeProps } from 'lib/components/Badge';
import { ColumnButton } from 'lib/components/ColumnButton';
import { InvoiceStatus } from 'lib/enums';
import {
  ArrowDownTrayIcon,
  CurrencyDollarIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';
import { isPublisherOrganization } from 'lib/utils/organizations';
import { dbToUICurrencyString } from 'lib/pricing/ui';
import { isUnpaidSearchableInvoice } from 'lib/utils/invoices';
import { INVOICE_STATUSES_FUNDS_PENDING } from 'lib/model/objects/invoiceModel';
import { ResponseOrError, wrapError, wrapSuccess } from 'lib/types/responses';
import { SearchableInvoiceRecord } from 'lib/types/searchable';
import { ColumnService } from 'lib/services/directory';
import { logAndCaptureException } from 'utils';
import { getInvoiceStatus } from './invoiceTableSettingsUtils';

export const downloadInvoice = async (
  invoiceId: string,
  isBulkInvoice: boolean
): Promise<ResponseOrError<string>> => {
  const {
    response,
    error
  }: EResponseTypes['documents/invoice/:invoiceId/download'] = await api.get(
    `documents/${
      isBulkInvoice ? 'bulk-invoice' : 'invoice'
    }/${invoiceId}/download`
  );

  if (error || !response) {
    return wrapError(new Error('Failed to download invoice'));
  }

  window.open(response);
  return wrapSuccess(response);
};

export const getInvoiceBadgeStatus = (
  invoiceStatus: number,
  due_date: number,
  voided: number,
  amount: number | undefined
) => {
  let status: BadgeProps['status'] = 'critical';
  let color = 'red';
  // in case of invoice has no line items or amount is 0
  if (!amount) {
    status = 'success';
    color = 'green';
  } else if (INVOICE_STATUSES_FUNDS_PENDING.includes(invoiceStatus)) {
    status = 'warning';
    color = 'yellow';
  } else if (voided || invoiceStatus === InvoiceStatus.uncollectible.value) {
    status = undefined;
    color = 'gray';
  } else if (
    invoiceStatus === InvoiceStatus.unpaid.value &&
    moment.utc(due_date * 1000).isBefore(moment.utc())
  ) {
    color = 'red';
    status = 'critical';
  } else if (invoiceStatus === InvoiceStatus.unpaid.value) {
    color = 'yellow';
    status = 'warning';
  } else if (
    [InvoiceStatus.paid.value, InvoiceStatus.partially_refunded.value].includes(
      invoiceStatus
    )
  ) {
    color = 'green';
    status = 'success';
  }

  return { status, color };
};

function NoticeNameCell({ invoice }: { invoice: SearchableInvoiceRecord }) {
  if (!invoice.noticeid) {
    return <td>{invoice.noticename}</td>;
  }

  return (
    <td>
      <a
        href={`/notice/${invoice.noticeid}`}
        target="_blank"
        rel="noreferrer"
        className="text-blue-700 underline cursor-pointer"
      >
        {invoice.noticename}
      </a>
    </td>
  );
}

export function InvoicesTableRow({
  invoice,
  organization,
  setSendReminderModalInvoice,
  setProcessPaymentModalInvoice
}: {
  invoice: SearchableInvoiceRecord;
  organization: ESnapshot<EOrganization> | null;
  setSendReminderModalInvoice: Function;
  setProcessPaymentModalInvoice: Function;
}) {
  const [isDownloading, setIsDownloading] = useState(false);
  const isInvoiceUnpaid = isUnpaidSearchableInvoice(invoice);
  const invoiceBadgeStatus = getInvoiceBadgeStatus(
    invoice.status,
    moment.utc(invoice.formattedduedate, 'MM/DD/YYYY').unix(),
    invoice.voided,
    invoice.nettotal
  ).status;

  const invoiceRowStatus = getInvoiceStatus(
    invoice.status,
    moment.utc(invoice.formattedduedate, 'MM/DD/YYYY').unix(),
    invoice.voided,
    invoice.nettotal
  );

  async function handleDownloadInvoice() {
    setIsDownloading(true);
    const { error } = await downloadInvoice(
      invoice.id,
      !!invoice.isbulkinvoice
    );

    if (error) {
      logAndCaptureException(
        ColumnService.PAYMENTS,
        error,
        'Error downloading invoice',
        { invoiceId: invoice.id }
      );
    }
    setIsDownloading(false);
  }

  if (isPublisherOrganization(organization)) {
    return (
      <>
        <td>
          <div>{invoice.filername}</div>
          <div>{invoice.invoicenumber}</div>
        </td>
        <NoticeNameCell invoice={invoice} />
        <td>{invoice.filerorganizationname || ''}</td>
        <td>{moment.utc(invoice.created).format('MM/DD/YYYY')}</td>
        <td>${dbToUICurrencyString(invoice.nettotal)}</td>
        <td>
          <Badge status={invoiceBadgeStatus}>{invoiceRowStatus}</Badge>
        </td>
        <td>{invoice.formattedduedate}</td>
        <td className="flex items-center py-4 gap-2">
          <ColumnButton
            tertiary
            aria-label="Send Reminder"
            size="sm"
            disabled={!isInvoiceUnpaid}
            onClick={() => {
              setSendReminderModalInvoice(invoice);
            }}
            startIcon={
              <EnvelopeIcon className="w-5 py-1 text-column-grey-400" />
            }
            type="button"
          />
          <ColumnButton
            tertiary
            aria-label="Process Payment"
            size="sm"
            disabled={!isInvoiceUnpaid}
            onClick={() => {
              setProcessPaymentModalInvoice(invoice);
            }}
            startIcon={
              <CurrencyDollarIcon className="w-5 py-1 text-column-grey-400" />
            }
            type="button"
          />
          <ColumnButton
            tertiary
            aria-label="Download Invoice"
            size="sm"
            loading={isDownloading}
            onClick={async e => {
              e.stopPropagation();
              await handleDownloadInvoice();
            }}
            startIcon={
              <ArrowDownTrayIcon className="w-5 py-1 text-column-grey-400" />
            }
            type="button"
          />
        </td>
      </>
    );
  }

  return (
    <>
      <td>{moment.utc(invoice.created).format('MM/DD/YYYY')}</td>
      <td>{invoice.invoicenumber}</td>
      <NoticeNameCell invoice={invoice} />
      <td>{invoice.publishername}</td>
      <td>${dbToUICurrencyString(invoice.nettotal)}</td>
      <td>
        <Badge status={invoiceBadgeStatus}>{invoiceRowStatus}</Badge>
      </td>
      <td>{invoice.formattedduedate}</td>
      <td className="flex items-center p-4 space-x-2 text-align">
        <ColumnButton
          buttonText={'Pay'}
          primary
          size="sm"
          onClick={e => {
            e.stopPropagation();
            window.open(`${window.location.origin}/invoices/${invoice.id}/pay`);
          }}
          disabled={!isInvoiceUnpaid}
          type="button"
        />
        <ColumnButton
          tertiary
          id="download-invoice"
          size="sm"
          loading={isDownloading}
          onClick={async e => {
            e.stopPropagation();
            await handleDownloadInvoice();
          }}
          startIcon={<ArrowDownTrayIcon className="w-4 text-column-grey-400" />}
          type="button"
          buttonText="Download"
        />
      </td>
    </>
  );
}
